import { useStore } from "../../store";
import { useQueryClient } from "@tanstack/react-query";
import { useApi } from "@lib/helpers/useApi";

import type {
    AddressResponse,
    MeAddressCreateRequest,
    MeAddressResponseResponseModel,
    MeAddressUpdateRequest,
} from "@interfaces/storefront-api";
import type { Customer } from "@store/types";
import { QueryKeys } from "@lib/enums/QueryKeys";
import { FetchServiceAxios } from "@lib/helpers/fetchServiceAxios";
import { useSession } from "next-auth/react";
import { useEffect } from "react";
import { invalidateProfileQueries } from "@store/helpers/profile/invalidateProfileQueries";

const getAddresses = async (): Promise<AddressResponse[]> => {
    return FetchServiceAxios.get<AddressResponse[]>(`/api/addresses/get-addresses`);
};

export const useGetMyAddresses = () => {
    const setData = useStore((state) => state.setMyAddresses);
    const { data: session, status } = useSession();

    const { data, isLoading, error } = useApi.useAsync<any>(
        [QueryKeys.getAddresses, session?.anonymousId, session?.ucid],
        () => {
            if (!session || !!session.anonymousId) {
                return undefined;
            }
            return getAddresses();
        },
        {
            enabled: status === "authenticated",
            retry: false,
        }
    );

    useEffect(() => {
        setData((data ?? {}) as Customer);
    }, [data]);

    return {
        data,
        isLoading,
        error,
    };
};

export const useAddAddressToCustomer = () => {
    const setData = useStore((state) => state.setMyAddresses);
    const queryClient = useQueryClient();

    const mutation = useApi.useMutate<MeAddressCreateRequest, MeAddressResponseResponseModel>(
        `/api/addresses/add-address`,
        {
            method: "POST",
            onSuccess: (data) => {
                // Update zustand store
                setData(data as Customer);
                // Invalidate and refetch addresses, so we have the new address in the list
                invalidateProfileQueries(queryClient);
            },
        }
    );
    return { addAddressMutation: mutation };
};

export const useUpdateAddressToCustomer = () => {
    const queryClient = useQueryClient();
    const setData = useStore((state) => state.setMyAddresses);

    const mutation = useApi.useMutate<MeAddressUpdateRequest, MeAddressResponseResponseModel>(
        `/api/addresses/update-address`,
        {
            method: "PUT",
            onSuccess: (data) => {
                // We need to match types from SFAPI and GraphQL
                setData(data as Customer);
                // Invalidate and refetch addresses, so we have the new address in the list
                invalidateProfileQueries(queryClient);
            },
        }
    );
    return { updateAddressMutation: mutation };
};

export const useRemoveAddressToCustomer = () => {
    const setData = useStore((state) => state.setMyAddresses);
    const queryClient = useQueryClient();
    // Ask BE why Swagger is not generating request/response types for delete-address
    const mutation = useApi.useMutate<any, any>(`/api/addresses/delete-address`, {
        method: "DELETE",
        onSuccess: (data) => {
            // Update zustand store
            setData(data as Customer);
            // Invalidate and refetch addresses, so we have the new address in the list
            invalidateProfileQueries(queryClient);
        },
    });
    return { deleteAddressMutation: mutation };
};
